import { useId, useRef, useEffect, useState, useCallback } from "react";
import { cn } from "@/lib/utils";

type PaintBrushDividerProps = {
  className?: string;
  variant?: "light" | "dark";
};

const PaintBrushDivider = ({ className, variant = "light" }: PaintBrushDividerProps) => {
  const uid = useId();
  const gradientId = `brushGradient-${uid}`;
  const maskId = `brushMask-${uid}`;
  const ref = useRef<HTMLDivElement>(null);
  const [displayProgress, setDisplayProgress] = useState(0);
  const targetProgress = useRef(0);
  const animationFrame = useRef<number>();
  
  // Creamy white for light variant, dark slate for dark variant
  const baseColor = variant === "light" ? "40 25% 88%" : "220 15% 22%";
  const darkColor = variant === "light" ? "35 20% 65%" : "220 18% 12%";
  const highlightColor = variant === "light" ? "45 35% 95%" : "220 10% 32%";

  // Smooth interpolation using requestAnimationFrame
  const animate = useCallback(() => {
    setDisplayProgress(current => {
      const diff = targetProgress.current - current;
      // Lerp with a small factor for smooth movement
      const newProgress = current + diff * 0.08;
      
      // Stop animating when close enough
      if (Math.abs(diff) < 0.001) {
        animationFrame.current = undefined;
        return targetProgress.current;
      }
      
      // Continue animation
      animationFrame.current = requestAnimationFrame(animate);
      return newProgress;
    });
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleScroll = () => {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Start animation when element enters viewport, complete when it's centered
      const startPoint = windowHeight;
      const endPoint = windowHeight * 0.3;
      
      let newTarget: number;
      if (rect.top >= startPoint) {
        newTarget = 0;
      } else if (rect.top <= endPoint) {
        newTarget = 1;
      } else {
        newTarget = 1 - (rect.top - endPoint) / (startPoint - endPoint);
        newTarget = Math.min(1, Math.max(0, newTarget));
      }
      
      targetProgress.current = newTarget;
      
      // Start animation loop if not already running
      if (animationFrame.current === undefined) {
        animationFrame.current = requestAnimationFrame(animate);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (animationFrame.current !== undefined) {
        cancelAnimationFrame(animationFrame.current);
        animationFrame.current = undefined;
      }
    };
  }, [animate]);

  // Apply subtle easing curve to displayed progress
  const easedProgress = displayProgress < 0.5 
    ? 2 * displayProgress * displayProgress 
    : 1 - Math.pow(-2 * displayProgress + 2, 2) / 2;

  return (
    <div ref={ref} className={cn("relative w-full h-16 -my-8 pointer-events-none z-10", className)}>
      <svg
        viewBox="0 0 1200 80"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          {/* Main gradient - lighter/smoother on left, darker/rougher on right */}
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={`hsl(${highlightColor})`} stopOpacity="0.4" />
            <stop offset="6%" stopColor={`hsl(${highlightColor})`} stopOpacity="1" />
            <stop offset="15%" stopColor={`hsl(${baseColor})`} stopOpacity="1" />
            <stop offset="50%" stopColor={`hsl(${baseColor})`} stopOpacity="1" />
            <stop offset="70%" stopColor={`hsl(${darkColor})`} stopOpacity="1" />
            <stop offset="88%" stopColor={`hsl(${darkColor})`} stopOpacity="0.8" />
            <stop offset="100%" stopColor={`hsl(${darkColor})`} stopOpacity="0.3" />
          </linearGradient>
          
          {/* Animated mask - reveals from left to right */}
          <mask id={maskId}>
            <rect 
              x="0" 
              y="0" 
              width={`${easedProgress * 100}%`} 
              height="100%" 
              fill="white"
            />
          </mask>
        </defs>

        <g mask={`url(#${maskId})`}>
          {/* Main brush stroke body - long rectangular with rounded leading edge, fragmented trailing */}
          <path
            d="M8,32 
               C2,36 2,44 8,48
               C20,52 60,48 120,46
               L1050,42
               C1080,41 1100,38 1120,36
               C1135,34 1148,32 1158,30
               L1162,28
               L1165,32
               C1155,36 1140,40 1120,44
               C1100,48 1080,50 1050,52
               L120,56
               C60,58 20,54 8,48
               Z"
            fill={`url(#${gradientId})`}
          />

          {/* Top edge - subtle organic waviness */}
          <path
            d="M12,30 
               C40,26 90,32 150,28 
               C220,24 300,30 400,26 
               C500,22 600,28 700,24 
               C800,20 900,26 1000,24
               C1060,22 1100,28 1130,26
               C1150,24 1162,22 1170,20"
            stroke={`hsl(${highlightColor})`}
            strokeWidth="3"
            fill="none"
            strokeOpacity="0.7"
            strokeLinecap="round"
          />

          {/* Bottom edge - subtle waviness */}
          <path
            d="M12,52 
               C40,56 100,50 180,54 
               C280,58 380,52 500,56 
               C620,60 720,54 820,58
               C900,62 980,56 1040,60
               C1080,64 1110,58 1135,62
               C1155,66 1170,62 1180,66"
            stroke={`hsl(${darkColor})`}
            strokeWidth="2.5"
            fill="none"
            strokeOpacity="0.6"
            strokeLinecap="round"
          />

          {/* Horizontal streaked paint texture */}
          <g strokeOpacity="0.35" stroke={`hsl(${highlightColor})`} strokeWidth="1">
            <line x1="20" y1="36" x2="1100" y2="34" />
            <line x1="15" y1="40" x2="1080" y2="40" />
            <line x1="18" y1="44" x2="1090" y2="46" />
            <line x1="22" y1="50" x2="1070" y2="52" />
          </g>

          {/* Darker breakup/fragmentation at trailing edge */}
          <g fill={`hsl(${darkColor})`}>
            <ellipse cx="1140" cy="36" rx="18" ry="6" fillOpacity="0.5" />
            <ellipse cx="1160" cy="44" rx="14" ry="5" fillOpacity="0.45" />
            <ellipse cx="1175" cy="32" rx="10" ry="4" fillOpacity="0.4" />
            <ellipse cx="1155" cy="54" rx="16" ry="5" fillOpacity="0.5" />
            <ellipse cx="1180" cy="50" rx="12" ry="4" fillOpacity="0.35" />
            <ellipse cx="1188" cy="40" rx="8" ry="3" fillOpacity="0.3" />
          </g>

          {/* Additional fragmented trailing pieces */}
          <g fill={`hsl(${darkColor})`} fillOpacity="0.25">
            <circle cx="1192" cy="36" r="3" />
            <circle cx="1195" cy="48" r="2.5" />
            <circle cx="1188" cy="58" r="2" />
            <ellipse cx="1178" cy="26" rx="6" ry="2" />
          </g>
        </g>
      </svg>
    </div>
  );
};

export default PaintBrushDivider;
